import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useOptimizedSupabaseScripts } from '@/hooks/useOptimizedSupabaseScripts';
import { templateActivationCache } from '@/utils/templateActivationCache';
import { applyStaticMergeTags } from '@/utils/mergeTags';
import {
  PhoneOutgoing,
  PhoneIncoming,
  ArrowRight,
  ArrowLeft,
  Save,
} from 'lucide-react';
import { Textarea } from './ui/textarea';

interface TemplateBrowserProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingScript?: any | null;
  onScriptCreated?: (script?: any) => void;
}

interface ScriptTemplate {
  id: string;
  name: string;
  category: string;
  description: string | null;
  content: string;
  first_message: string | null;
  created_at: string;
}

interface UserFormData {
  scriptName: string;
  companyName: string;
  userName: string;
  userTitle: string;
  industry: string;
  businessType: string;
  hours: string;
  location: string;
  services: string;
  customFirstMessage?: string;
}

type Step = 'type' | 'template' | 'details';

// ←←← YOUR DEFAULTS — change these to whatever you want
const DEFAULT_VALUES = {
  companyName: 'Your Company',
  userName: 'Your Name',
  userTitle: 'Sales Representative',
  industry: 'Your Industry',
  businessType: 'Your Business Type',
  hours: 'Mon–Fri 9AM–5PM',
  location: 'We are remote and located in many areas.',
  services: 'Your Services',
};

export function TemplateBrowser({
  open,
  onOpenChange,
  editingScript = null,
  onScriptCreated,
}: TemplateBrowserProps) {
  const { toast } = useToast();
  const { addScript, updateScript, refreshScripts } =
    useOptimizedSupabaseScripts();

  const [step, setStep] = useState<Step>(editingScript ? 'details' : 'type');
  const [selectedType, setSelectedType] = useState<
    'inbound' | 'outbound' | null
  >(editingScript?.mode || null);
  const [selectedTemplate, setSelectedTemplate] =
    useState<ScriptTemplate | null>(null);

  const [templates, setTemplates] = useState<ScriptTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState<UserFormData>({
    scriptName: '',
    companyName: '',
    userName: '',
    userTitle: '',
    industry: '',
    businessType: '',
    hours: '',
    location: '',
    services: '',
    customFirstMessage: '',
  });

  const [previewContent, setPreviewContent] = useState('');

  // Initialize editing mode
  useEffect(() => {
    if (!editingScript || !open) return;

    const sections = editingScript.script_sections || {};
    const mv = sections.merge_values || {};

    setFormData({
      scriptName: editingScript.name || '',
      companyName: mv.companyName || editingScript.company_name || '',
      userName: mv.userName || editingScript.user_name || '',
      userTitle: mv.userTitle || editingScript.user_title || '',
      industry: mv.industry || editingScript.target_industry || '',
      businessType: mv.businessType || editingScript.safe_mode_service || '',
      hours: mv.hours || '',
      location: mv.location || '',
      services: mv.services || '',
      customFirstMessage:
        editingScript.mode === 'inbound'
          ? editingScript.first_message || ''
          : '',
    });

    setPreviewContent(
      sections.unresolved_content || editingScript.content || ''
    );
    setSelectedType(editingScript.mode);
    setStep('details');
  }, [editingScript, open]);

  // Live preview as user types
  useEffect(() => {
    if (!open) return;

    const baseContent =
      editingScript?.script_sections?.unresolved_content ||
      selectedTemplate?.content ||
      editingScript?.content ||
      '';

    const merged = applyStaticMergeTags(baseContent, {
      companyName: formData.companyName || DEFAULT_VALUES.companyName,
      userName: formData.userName || DEFAULT_VALUES.userName,
      userTitle: formData.userTitle || DEFAULT_VALUES.userTitle,
      industry: formData.industry || DEFAULT_VALUES.industry,
      businessType: formData.businessType || DEFAULT_VALUES.businessType,
      hours: formData.hours || DEFAULT_VALUES.hours,
      location: formData.location || DEFAULT_VALUES.location,
      services: formData.services || DEFAULT_VALUES.services,
    });

    setPreviewContent(merged);
  }, [
    editingScript?.script_sections?.unresolved_content,
    selectedTemplate?.content,
    formData.companyName,
    formData.userName,
    formData.userTitle,
    formData.industry,
    formData.businessType,
    formData.hours,
    formData.location,
    formData.services,
    open,
  ]);

  // Apply defaults when creating new script
  useEffect(() => {
    if (open && !editingScript && step === 'details') {
      setFormData((prev) => ({
        ...prev,
        companyName: prev.companyName || DEFAULT_VALUES.companyName,
        userName: prev.userName || DEFAULT_VALUES.userName,
        userTitle: prev.userTitle || DEFAULT_VALUES.userTitle,
        industry: prev.industry || DEFAULT_VALUES.industry,
        businessType: prev.businessType || DEFAULT_VALUES.businessType,
        hours: prev.hours || DEFAULT_VALUES.hours,
        location: prev.location || DEFAULT_VALUES.location,
        services: prev.services || DEFAULT_VALUES.services,
      }));
    }
  }, [open, editingScript, step]);

  // Fetch templates when type is selected
  useEffect(() => {
    if (selectedType && step === 'template') {
      fetchTemplates();
    }
  }, [selectedType, step]);

  const fetchTemplates = async () => {
    if (!selectedType) return;

    setIsLoading(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('is_admin')
        .eq('user_id', user.id)
        .maybeSingle();

      const isAdmin = profile?.is_admin === true;

      const { data, error } = await supabase
        .from('script_templates')
        .select('*')
        .eq('category', selectedType)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const templateIds = (data || []).map((t) => t.id);
      const { activationMap, publicMap } =
        await templateActivationCache.getTemplateStatuses(templateIds);

      let activeTemplates = (data || []).filter(
        (t) => activationMap[t.id] === true
      );

      if (!isAdmin) {
        activeTemplates = activeTemplates.filter(
          (t) => publicMap[t.id] === true
        );
      }

      setTemplates(activeTemplates);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load templates',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTypeSelect = (type: 'inbound' | 'outbound') => {
    setSelectedType(type);
    setSelectedTemplate(null);
    setStep('template');
  };

  const handleTemplateSelect = (template: ScriptTemplate) => {
    if (selectedType === 'inbound' && !template.first_message?.trim()) {
      toast({
        title: 'Invalid Template',
        description: 'Inbound templates must have a greeting message.',
        variant: 'destructive',
      });
      return;
    }

    setSelectedTemplate(template);
    setStep('details');
  };

  const handleSave = async () => {
    if (!formData.scriptName.trim()) {
      toast({
        title: 'Error',
        description: 'Script name required',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);
    try {
      // Final merge values with defaults (only if blank)
      const finalMergeValues = {
        companyName: formData.companyName.trim() || DEFAULT_VALUES.companyName,
        userName: formData.userName.trim() || DEFAULT_VALUES.userName,
        userTitle: formData.userTitle.trim() || DEFAULT_VALUES.userTitle,
        industry: formData.industry.trim() || DEFAULT_VALUES.industry,
        businessType:
          formData.businessType.trim() || DEFAULT_VALUES.businessType,
        hours: formData.hours.trim() || DEFAULT_VALUES.hours,
        location: formData.location.trim() || DEFAULT_VALUES.location,
        services: formData.services.trim() || DEFAULT_VALUES.services,
      };

      const originalUnresolved =
        selectedTemplate?.content ||
        editingScript?.script_sections?.unresolved_content ||
        editingScript?.content ||
        '';

      let unresolvedContent = originalUnresolved;

      const isLegacyScript =
        !editingScript?.script_sections?.unresolved_content;

      if (isLegacyScript && editingScript?.content) {
        let restored = editingScript.content;

        if (formData.companyName.trim())
          restored = restored
            .split(formData.companyName.trim())
            .join('{{companyName}}');
        if (formData.userName.trim())
          restored = restored
            .split(formData.userName.trim())
            .join('{{userName}}');
        if (formData.userTitle.trim())
          restored = restored
            .split(formData.userTitle.trim())
            .join('{{userTitle}}');
        if (formData.industry.trim())
          restored = restored
            .split(formData.industry.trim())
            .join('{{industry}}');
        if (formData.businessType.trim())
          restored = restored
            .split(formData.businessType.trim())
            .join('{{businessType}}');
        if (formData.hours.trim())
          restored = restored.split(formData.hours.trim()).join('{{hours}}');
        if (formData.location.trim())
          restored = restored
            .split(formData.location.trim())
            .join('{{location}}');
        if (formData.services.trim())
          restored = restored
            .split(formData.services.trim())
            .join('{{services}}');

        unresolvedContent = restored;
      }

      const resolvedContent = applyStaticMergeTags(
        unresolvedContent,
        finalMergeValues
      );

      let finalFirstMessage: string | null = null;
      const isInbound =
        selectedType === 'inbound' || editingScript?.mode === 'inbound';

      if (isInbound) {
        if (formData.customFirstMessage?.trim()) {
          finalFirstMessage = formData.customFirstMessage.trim();
        } else if (selectedTemplate?.first_message) {
          finalFirstMessage = applyStaticMergeTags(
            selectedTemplate.first_message,
            finalMergeValues
          );
        } else if (editingScript?.first_message) {
          finalFirstMessage = editingScript.first_message;
        }
      }

      const scriptSections = {
        template_id:
          selectedTemplate?.id ||
          editingScript?.script_sections?.template_id ||
          null,
        unresolved_content: unresolvedContent,
        merge_values: finalMergeValues,
      };

      const scriptData = {
        name: formData.scriptName,
        mode: selectedType || editingScript?.mode || 'outbound',
        target_industry: formData.industry,
        content: resolvedContent,
        first_message: finalFirstMessage,
        tone: 'professional',
        llm_provider: 'openai',
        llm_model: 'gpt-4o-mini',
        company_name: finalMergeValues.companyName,
        user_name: finalMergeValues.userName,
        user_title: finalMergeValues.userTitle,
        safe_mode_service: finalMergeValues.businessType,
        safe_mode: false,
        use_low_risk_mode: false,
        export_enabled: true,
        objections: [],
        questions: [],
        script_sections: scriptSections,
      };

      let savedScript;

      if (editingScript) {
        savedScript = await updateScript?.(editingScript.id, scriptData);
        toast({ title: 'Success', description: 'Script updated successfully' });
      } else {
        savedScript = await addScript?.(scriptData);
        toast({ title: 'Success', description: 'Script created successfully' });
      }

      await refreshScripts?.();
      onScriptCreated?.(savedScript);
      onOpenChange(false);
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to save script',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const resetForm = () => {
    setStep('type');
    setSelectedType(null);
    setSelectedTemplate(null);
    setFormData({
      scriptName: '',
      companyName: '',
      userName: '',
      userTitle: '',
      industry: '',
      businessType: '',
      hours: '',
      location: '',
      services: '',
    });
    setPreviewContent('');
  };

  const handleClose = () => {
    onOpenChange(false);
    resetForm();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className='max-w-6xl h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            {editingScript ? 'Edit Script' : 'Create Script from Template'}
          </DialogTitle>
        </DialogHeader>

        <div className='space-y-6'>
          {/* Step Navigation */}
          <div className='flex items-center justify-center space-x-4'>
            <div
              className={`flex items-center space-x-2 ${
                step === 'type'
                  ? 'text-primary'
                  : selectedType
                  ? 'text-green-600'
                  : 'text-muted-foreground'
              }`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                  step === 'type'
                    ? 'border-primary bg-primary text-white'
                    : selectedType
                    ? 'border-green-600 bg-green-600 text-white'
                    : 'border-muted-foreground'
                }`}
              >
                1
              </div>
              <span className='font-medium'>Script Type</span>
            </div>

            <ArrowRight className='h-4 w-4 text-muted-foreground' />

            <div
              className={`flex items-center space-x-2 ${
                step === 'template'
                  ? 'text-primary'
                  : selectedTemplate
                  ? 'text-green-600'
                  : 'text-muted-foreground'
              }`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                  step === 'template'
                    ? 'border-primary bg-primary text-white'
                    : selectedTemplate
                    ? 'border-green-600 bg-green-600 text-white'
                    : 'border-muted-foreground'
                }`}
              >
                2
              </div>
              <span className='font-medium'>Template</span>
            </div>

            <ArrowRight className='h-4 w-4 text-muted-foreground' />

            <div
              className={`flex items-center space-x-2 ${
                step === 'details' ? 'text-primary' : 'text-muted-foreground'
              }`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                  step === 'details'
                    ? 'border-primary bg-primary text-white'
                    : 'border-muted-foreground'
                }`}
              >
                3
              </div>
              <span className='font-medium'>Details & Preview</span>
            </div>
          </div>

          {/* Card 1: Script Type Selection */}
          {step === 'type' && !editingScript && (
            <Card>
              <CardHeader>
                <CardTitle>Choose Script Type</CardTitle>
              </CardHeader>
              <CardContent>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <button
                    onClick={() => handleTypeSelect('outbound')}
                    className='p-6 border rounded-lg hover:border-primary hover:bg-accent transition-colors text-left'
                  >
                    <div className='flex items-center gap-3 mb-3'>
                      <PhoneOutgoing className='h-6 w-6 text-primary' />
                      <h3 className='font-semibold'>Outbound Scripts</h3>
                    </div>
                    <p className='text-sm text-muted-foreground'>
                      For making cold calls to prospects and generating new
                      business opportunities.
                    </p>
                  </button>

                  <button
                    onClick={() => handleTypeSelect('inbound')}
                    className='p-6 border rounded-lg hover:border-primary hover:bg-accent transition-colors text-left'
                  >
                    <div className='flex items-center gap-3 mb-3'>
                      <PhoneIncoming className='h-6 w-6 text-success' />
                      <h3 className='font-semibold'>Inbound Scripts</h3>
                    </div>
                    <p className='text-sm text-muted-foreground'>
                      For handling incoming calls from leads and existing
                      customers.
                    </p>
                  </button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Card 2: Template Selection */}
          {step === 'template' && selectedType && (
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  {selectedType === 'outbound' ? (
                    <PhoneOutgoing className='h-5 w-5 text-primary' />
                  ) : (
                    <PhoneIncoming className='h-5 w-5 text-success' />
                  )}
                  Select {selectedType === 'outbound' ? 'Outbound' : 'Inbound'}{' '}
                  Template
                </CardTitle>
                <div className='flex gap-2'>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => setStep('type')}
                  >
                    <ArrowLeft className='h-4 w-4 mr-1' />
                    Back
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className='flex items-center justify-center py-8'>
                    <div className='text-center'>
                      <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto'></div>
                      <p className='mt-2 text-muted-foreground'>
                        Loading templates...
                      </p>
                    </div>
                  </div>
                ) : templates.length === 0 ? (
                  <div className='text-center py-8'>
                    <p className='text-muted-foreground'>
                      No active templates available for {selectedType} scripts.
                    </p>
                    <p className='text-sm text-muted-foreground mt-2'>
                      Contact your administrator to add templates.
                    </p>
                  </div>
                ) : (
                  <div className='grid gap-4'>
                    {templates.map((template) => (
                      <div
                        key={template.id}
                        onClick={() => handleTemplateSelect(template)}
                        className={`p-4 border rounded-lg cursor-pointer hover:border-primary hover:bg-accent transition-colors ${
                          selectedTemplate?.id === template.id
                            ? 'border-primary bg-accent'
                            : ''
                        }`}
                      >
                        <div className='flex justify-between items-start'>
                          <div>
                            <h3 className='font-medium'>{template.name}</h3>
                            <Badge variant='outline' className='mt-1'>
                              {template.category}
                            </Badge>
                            {template.description && (
                              <p className='text-sm text-muted-foreground mt-2'>
                                {template.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Card 3: Details & Preview */}
          {step === 'details' && (selectedTemplate || editingScript) && (
            <Card>
              <CardHeader>
                <CardTitle>Add Details & Preview</CardTitle>
                {!editingScript && (
                  <div className='flex gap-2'>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => setStep('template')}
                    >
                      <ArrowLeft className='h-4 w-4 mr-1' />
                      Back
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                  {/* Form Section */}
                  <div className='space-y-4'>
                    <div>
                      <Label htmlFor='scriptName'>Script Name *</Label>
                      <Input
                        id='scriptName'
                        value={formData.scriptName}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            scriptName: e.target.value,
                          }))
                        }
                        placeholder='e.g., Main Reception Script'
                      />
                    </div>

                    <div>
                      <Label htmlFor='companyName'>Company Name *</Label>
                      <Input
                        id='companyName'
                        value={formData.companyName}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            companyName: e.target.value,
                          }))
                        }
                        placeholder='e.g., Acme Solutions'
                      />
                    </div>

                    <div>
                      <Label htmlFor='userName'>Agent Name *</Label>
                      <Input
                        id='userName'
                        value={formData.userName}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            userName: e.target.value,
                          }))
                        }
                        placeholder='e.g., Sarah'
                      />
                    </div>

                    <div>
                      <Label htmlFor='userTitle'>Agent Title</Label>
                      <Input
                        id='userTitle'
                        value={formData.userTitle}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            userTitle: e.target.value,
                          }))
                        }
                        placeholder='e.g., Sales Consultant'
                      />
                    </div>

                    <div>
                      <Label htmlFor='industry'>Industry</Label>
                      <Input
                        id='industry'
                        value={formData.industry}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            industry: e.target.value,
                          }))
                        }
                        placeholder='e.g., Technology, Healthcare, Finance'
                      />
                      <p className='text-sm text-muted-foreground mt-1'>
                        Target business vertical for analytics and reporting
                      </p>
                    </div>

                    <div>
                      <Label htmlFor='businessType'>Business Type</Label>
                      <Input
                        id='businessType'
                        value={formData.businessType}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            businessType: e.target.value,
                          }))
                        }
                        placeholder='e.g., marketing agency, law firm, medical practice'
                      />
                      <p className='text-sm text-muted-foreground mt-1'>
                        Plain-English description used for call context and
                        off-topic detection
                      </p>
                    </div>

                    <div>
                      <span className='text-sm mt-1'>Note: </span>
                      <span className='text-sm text-muted-foreground mt-1'>
                        The new fields below will work on newly created
                        templates. For older templates save will attempt to add
                        editable fields but isn't gauranteed.
                      </span>
                    </div>

                    {/* NEW CUSTOM FIELDS */}
                    <div>
                      <Label>Business Hours</Label>
                      <Input
                        value={formData.hours}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            hours: e.target.value,
                          }))
                        }
                        placeholder='Mon–Fri 9AM–5PM'
                      />
                    </div>
                    <div>
                      <Label>Location</Label>
                      <Input
                        value={formData.location}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            location: e.target.value,
                          }))
                        }
                        placeholder='Miami, FL'
                      />
                    </div>
                    <div>
                      <Label>Services Offered</Label>
                      <Input
                        value={formData.services}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            services: e.target.value,
                          }))
                        }
                        placeholder='Example: Roof Repair, Gutter Cleaning'
                      />
                    </div>

                    {/* CUSTOM GREETING – ONLY FOR INBOUND */}
                    {(selectedType === 'inbound' ||
                      editingScript?.mode === 'inbound') && (
                      <div className='pt-4 border-t'>
                        <Label className='font-semibold text-primary'>
                          Custom First Message (Greeting)
                        </Label>
                        <Textarea
                          value={formData.customFirstMessage}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              customFirstMessage: e.target.value,
                            }))
                          }
                          rows={4}
                          className='mt-2 font-medium'
                          placeholder='Hello! Thanks for calling {{companyName}}. This is {{userName}} speaking...'
                        />
                        <p className='text-xs text-muted-foreground mt-1'>
                          This is the exact message the AI will say first. Edit
                          or use the default template.
                        </p>
                      </div>
                    )}

                    <Button
                      onClick={handleSave}
                      disabled={isSaving || !formData.scriptName.trim()}
                      className='w-full'
                    >
                      <Save className='h-4 w-4 mr-2' />
                      {isSaving
                        ? 'Saving...'
                        : editingScript
                        ? 'Update Script'
                        : 'Create Script'}
                    </Button>
                  </div>

                  {/* Preview Section */}
                  <div>
                    <Label>Preview with Your Details</Label>
                    <div className='mt-2'>
                      <textarea
                        value={previewContent}
                        readOnly
                        className='w-full h-[500px] p-4 border rounded-lg font-mono text-sm resize-none bg-muted/30'
                      />
                      <p className='text-xs text-muted-foreground mt-2'>
                        This is exactly what the AI will say. All merge tags are
                        replaced live.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
